import { mediaUrl } from "./mediaUrl";

function stripQuery(path: string): string {
  return String(path).split("?")[0];
}

function basename(path: string): string {
  const clean = stripQuery(path).replace(/\\/g, "/");
  let decoded = clean;
  try {
    decoded = decodeURIComponent(clean);
  } catch {
    decoded = clean;
  }
  const parts = decoded.split("/").filter(Boolean);
  return (parts[parts.length - 1] || decoded).toLowerCase();
}

type ProductImage = { file_name: string; document_id?: number };

/** Normalize API image payloads into `{ file_name }[]` with absolute URLs available via mediaUrl. */
export function normalizeProductImages(raw: any): ProductImage[] {
  if (raw == null) return [];

  const list = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? [raw]
      : raw.images
        ? raw.images
        : raw.image
          ? [raw.image]
          : [];

  return list
    .map((img: any) => {
      if (!img) return null;
      if (typeof img === "string") return { file_name: img };
      const path = img.url || img.file_name || img.path || img.image || null;
      if (!path) return null;
      const documentId = img.document_id ?? img.documentId;
      return {
        file_name: String(path),
        document_id: documentId == null ? undefined : Number(documentId),
      };
    })
    .filter(Boolean) as ProductImage[];
}

/** Keep the API primary photo first. Match by document_id, never by URL substring. */
export function orderedProductImages(raw: any): ProductImage[] {
  const listed =
    Array.isArray(raw?.images) && raw.images.length > 0 ? raw.images : raw?.image ?? raw;
  const images = normalizeProductImages(listed);
  if (images.length === 0) {
    if (typeof raw?.image === "string" && raw.image.trim()) {
      return [{ file_name: raw.image.trim() }];
    }
    return [];
  }

  const primaryId = raw?.image_document_id;
  if (primaryId != null && primaryId !== "") {
    const idx = images.findIndex(
      (img) => img.document_id != null && Number(img.document_id) === Number(primaryId)
    );
    if (idx > 0) {
      const next = [...images];
      const [hit] = next.splice(idx, 1);
      return [hit, ...next];
    }
    if (idx === 0) return images;
  }

  const primary =
    typeof raw?.image === "string" && raw.image.trim() ? basename(raw.image.trim()) : "";
  if (!primary) return images;

  const idx = images.findIndex((img) => basename(img.file_name) === primary);
  if (idx <= 0) return images;
  const next = [...images];
  const [hit] = next.splice(idx, 1);
  return [hit, ...next];
}

export function primaryProductImage(raw: any): string {
  if (raw && typeof raw.image === "string" && raw.image.trim()) {
    return mediaUrl(raw.image) || "/Assets/no-pictures.png";
  }
  const images = orderedProductImages(raw);
  if (images.length === 0) return "/Assets/no-pictures.png";
  return mediaUrl(images[0].file_name) || "/Assets/no-pictures.png";
}
